import { ListenableSubscribable } from '@benzinga/subscribable';
import { SafeError, SafePromise } from '@benzinga/safe-await';
import { Session } from '@benzinga/session';
import { AutocompleteFundRestful, AutocompleteSymbolRestful } from './restful';
import { FundsResult, SymbolsResult } from './entities';

interface AutocompleteErrorEvent {
  error?: SafeError;
  errorType: 'get_funds_error' | 'get_symbols_error';
  type: 'error';
}
interface AutocompleteSymbolsResultsEvent {
  result: SymbolsResult;
  type: 'autocomplete_symbols_results';
}

interface AutocompleteFundsResultsEvent {
  result: FundsResult;
  type: 'autocomplete_funds_results';
}

export type AutocompleteRequestEvent =
  | AutocompleteErrorEvent
  | AutocompleteSymbolsResultsEvent
  | AutocompleteFundsResultsEvent;

/**
 * Responsible for handling of restful requests for autocomplete funds and symbols,
 * and making event calls for a single response's result or error
 * @remarks methods are returning returning response list
 */
export class AutocompleteRequest extends ListenableSubscribable<AutocompleteRequestEvent> {
  private restfulFund: AutocompleteFundRestful;
  private restfulSymbol: AutocompleteSymbolRestful;

  constructor(session: Session) {
    super();
    this.restfulFund = new AutocompleteFundRestful(session);
    this.restfulSymbol = new AutocompleteSymbolRestful(session);
  }

  /**
   * @param query - input query
   * @param limit - number of results
   * @returns - list of symbols
   */
  public getAutoCompleteSymbols = async (query: string, limit = 100): SafePromise<SymbolsResult> => {
    const autoComplete = await this.restfulSymbol.getAutoCompleteSymbols(query, limit);
    if (autoComplete.err) {
      this.dispatch({
        error: autoComplete.err,
        errorType: 'get_symbols_error',
        type: 'error',
      });
    } else {
      this.dispatch({
        result: autoComplete.ok,
        type: 'autocomplete_symbols_results',
      });
    }
    return autoComplete;
  };

  /**
   *
   * @param query - input query
   * @param limit - number of results
   * @returns - list of funds
   */
  public getAutoCompleteFunds = async (query: string, limit = 100): SafePromise<FundsResult> => {
    const autoComplete = await this.restfulFund.getAutoCompleteFunds(query, limit);
    if (autoComplete.err) {
      this.dispatch({
        error: autoComplete.err,
        errorType: 'get_symbols_error',
        type: 'error',
      });
    } else {
      this.dispatch({
        result: autoComplete.ok,
        type: 'autocomplete_funds_results',
      });
    }
    return autoComplete;
  };
}
